import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define service needs and requirement types
export type BusinessSize = 'solo' | 'small' | 'medium' | 'enterprise';
export type AIExperience = 'none' | 'beginner' | 'intermediate' | 'advanced';
export type Implementation = 'quick' | 'thorough' | 'comprehensive';
export type Budget = 'low' | 'medium' | 'high';
export type PrimaryGoal = 
  | 'skill-development' 
  | 'process-automation' 
  | 'custom-solution' 
  | 'system-optimization' 
  | 'business-integration';

export interface ServiceRequirements {
  businessSize: BusinessSize;
  aiExperience: AIExperience;
  implementation: Implementation;
  budget: Budget;
  primaryGoal: PrimaryGoal;
}

export interface ServiceRecommendation {
  primaryService: string;
  secondaryService: string;
  description: string;
  link: string;
}

// Default values
const defaultRequirements: ServiceRequirements = {
  businessSize: 'small',
  aiExperience: 'beginner',
  implementation: 'quick',
  budget: 'medium',
  primaryGoal: 'process-automation',
};

interface ServiceSelectorContextType {
  requirements: ServiceRequirements;
  updateRequirements: (requirements: Partial<ServiceRequirements>) => void;
  recommendation: ServiceRecommendation | null;
  generateRecommendation: () => void;
  resetSelector: () => void;
}

// Create the context
const ServiceSelectorContext = createContext<ServiceSelectorContextType | undefined>(undefined);

// Provider component
interface ServiceSelectorProviderProps {
  children: ReactNode;
}

export const ServiceSelectorProvider: React.FC<ServiceSelectorProviderProps> = ({ children }) => {
  const [requirements, setRequirements] = useState<ServiceRequirements>(defaultRequirements);
  const [recommendation, setRecommendation] = useState<ServiceRecommendation | null>(null);

  // Update requirements with partial data
  const updateRequirements = (newRequirements: Partial<ServiceRequirements>) => {
    setRequirements((prev) => ({ ...prev, ...newRequirements }));
  };

  // Reset to default
  const resetSelector = () => {
    setRequirements(defaultRequirements);
    setRecommendation(null);
  };

  // Generate recommendation based on requirements
  const generateRecommendation = () => {
    const { businessSize, aiExperience, primaryGoal, budget } = requirements;
    
    // Logic to determine primary service based on user inputs
    let primaryService = '';
    let secondaryService = '';
    let description = '';
    let link = '';
    
    // Primary goal is the most important factor
    if (primaryGoal === 'skill-development') {
      primaryService = 'AI Training & Education';
      link = '/services/ai-training';
      
      if (businessSize === 'solo' || businessSize === 'small') {
        description = 'Our personalized AI training program is ideal for individuals and small teams looking to build AI skills.';
        secondaryService = 'Custom AI Development';
      } else {
        description = 'Our enterprise AI training solutions can upskill your entire workforce with tailored learning paths.';
        secondaryService = 'Business AI Onboarding';
      }
    } 
    else if (primaryGoal === 'process-automation') {
      primaryService = 'Workflow Automation';
      link = '/services/workflow-automation';
      
      if (budget === 'low') {
        description = 'Our workflow automation solutions can help streamline your business processes without breaking the bank.';
        secondaryService = 'AI Training & Education';
      } else {
        description = 'We can build comprehensive automation systems tailored to your unique business processes.';
        secondaryService = 'Business AI Onboarding';
      }
    }
    else if (primaryGoal === 'custom-solution') {
      primaryService = 'Custom AI Development';
      link = '/services/custom-ai-development';
      
      if (aiExperience === 'none' || aiExperience === 'beginner') {
        description = 'We can build a custom AI solution from scratch, handling all technical complexity for you.';
        secondaryService = 'AI Training & Education';
      } else {
        description = 'Our experts can collaborate with your technical team to develop specialized AI solutions.';
        secondaryService = 'AI System Optimization';
      }
    }
    else if (primaryGoal === 'system-optimization') {
      primaryService = 'AI System Optimization';
      link = '/services/ai-optimization';
      
      if (aiExperience === 'advanced') {
        description = 'We can tune and optimize your existing AI systems for maximum performance and efficiency.';
        secondaryService = 'Custom AI Development';
      } else {
        description = 'Our team can evaluate your AI implementations and provide optimization strategies with ongoing support.';
        secondaryService = 'Business AI Onboarding';
      }
    }
    else if (primaryGoal === 'business-integration') {
      primaryService = 'Business AI Onboarding';
      link = '/services/ai-onboarding';
      
      if (businessSize === 'enterprise') {
        description = 'Our enterprise AI integration solutions help large organizations implement AI across departments.';
        secondaryService = 'AI System Optimization';
      } else {
        description = 'We can help smaller businesses adopt AI technologies with practical integration strategies.';
        secondaryService = 'Workflow Automation';
      }
    }
    
    setRecommendation({
      primaryService,
      secondaryService,
      description,
      link,
    });
  };

  return (
    <ServiceSelectorContext.Provider value={{
      requirements,
      updateRequirements,
      recommendation,
      generateRecommendation,
      resetSelector,
    }}>
      {children}
    </ServiceSelectorContext.Provider>
  );
};

// Custom hook to use the service selector context
export const useServiceSelector = () => {
  const context = useContext(ServiceSelectorContext);
  if (context === undefined) {
    throw new Error('useServiceSelector must be used within a ServiceSelectorProvider');
  }
  return context;
};
